import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ActivityStatusTabs, { ACTIVITY_STATUS_TABS } from '../../components/ActivityStatusTabs';
import { HomeNavButton } from '../../components/NavButtons';
import { getStudentHomeworks } from '../../services/homeworkService';
import { focusTheme, getListCardProps } from '../../utils/focusTheme';

const statusConfig = {
  submitted: {
    ...focusTheme.status.submitted,
    label: 'تمرین حل شده',
  },
  published: {
    ...focusTheme.status.published,
    label: 'شروع تمرین',
  },
  expired: {
    ...focusTheme.status.expired,
    label: 'مهلت گذشته',
  },
};

function ArrowCircleIcon({ className = 'text-teal-500' }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <path d="M8.63612 5.41078L6.69336 7.21477C6.38802 7.4983 6.38802 7.98157 6.69336 8.2651L8.63612 10.0691M7.70447 0.537475C3.74645 0.537475 0.537843 3.74608 0.537843 7.7041C0.537843 11.6621 3.74645 14.8707 7.70447 14.8707C11.6625 14.8707 14.8711 11.6621 14.8711 7.7041C14.8711 3.74608 11.6625 0.537475 7.70447 0.537475Z" stroke="currentColor" strokeWidth="1.07499" strokeLinecap="round" />
    </svg>
  );
}

function formatHomeworkDate(value) {
  if (!value) {
    return 'بدون تاریخ';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return 'بدون تاریخ';
  }

  return new Intl.DateTimeFormat('fa-IR', {
    day: 'numeric',
    month: 'long',
  }).format(date);
}

function getHomeworkViewStatus(homework) {
  if (homework.submission) {
    return 'submitted';
  }

  if (homework.dueAt && new Date(homework.dueAt).getTime() < Date.now()) {
    return 'expired';
  }

  return homework.status || 'published';
}


function HomeworksPage() {
  const [homeworks, setHomeworks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [activeTab, setActiveTab] = useState(ACTIVITY_STATUS_TABS.remaining);

  const remainingHomeworks = useMemo(
    () => homeworks.filter((homework) => getHomeworkViewStatus(homework) !== 'submitted'),
    [homeworks]
  );
  const completedHomeworks = useMemo(
    () => homeworks.filter((homework) => getHomeworkViewStatus(homework) === 'submitted'),
    [homeworks]
  );
  const visibleHomeworks = activeTab === ACTIVITY_STATUS_TABS.completed ? completedHomeworks : remainingHomeworks;
  const remainingCount = remainingHomeworks.length;
  const completedCount = completedHomeworks.length;
  const nextHomework = remainingHomeworks[0];

  useEffect(() => {
    let isMounted = true;

    async function loadHomeworks() {
      setIsLoading(true);
      setErrorMessage('');

      try {
        const homeworkItems = await getStudentHomeworks();

        if (isMounted) {
          setHomeworks(homeworkItems);
        }
      } catch (error) {
        if (isMounted) {
          setErrorMessage(error.message || 'امکان دریافت تکالیف وجود ندارد.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadHomeworks();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div className='flex items-center gap-2'>
          <p
            style={{ background: focusTheme.header.background, boxShadow: focusTheme.header.boxShadow }}
            className='px-6 py-1 rounded-[10px] text-[15px] text-white'
          >
            تکالیف من
          </p>

          <img className='h-[50px]' src='/images/homework.webp' alt='' />
        </div>

        <HomeNavButton />
      </div>

      <div>
        <p className='text-[14px] font-semibold text-slate-900'>
          لیست تکالیف
        </p>

        <p className='mt-1 text-[13px] font-medium leading-6 text-slate-800'>
          تکلیف امروزت رو انتخاب کن و سوالاتش رو با دقت جواب بده.
        </p>
      </div>

      <ActivityStatusTabs
        remainingCount={remainingCount}
        completedCount={completedCount}
        remainingLabel="انجام نشده"
        completedLabel="انجام شده"
        activeTab={activeTab}
        onChange={setActiveTab}
      />

      <div
        className='flex flex-col gap-2 rounded-[8px] p-2'
        style={{ background: focusTheme.sectionBg }}
      >
        {isLoading ? (
          <div className="rounded-[8px] bg-white px-4 py-6 text-center text-[12px] text-slate-500">
            در حال دریافت تکالیف...
          </div>
        ) : errorMessage ? (
          <div className="rounded-[8px] bg-white px-4 py-5 text-center">
            <p className={`text-[12px] font-bold ${focusTheme.message.error}`}>{errorMessage}</p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className={`mt-3 rounded-[6px] px-5 py-1 text-[12px] font-bold ${focusTheme.button.retry}`}
            >
              تلاش دوباره
            </button>
          </div>
        ) : homeworks.length === 0 ? (
          <div className="rounded-[8px] bg-white px-4 py-6 text-center">
            <p className="text-[13px] font-bold text-slate-800">فعلا تکلیفی نداری</p>
            <p className="mt-2 text-[11px] text-slate-500">وقتی تکلیف جدید منتشر شود، همینجا نمایش داده می‌شود.</p>
          </div>
        ) : visibleHomeworks.length === 0 ? (
          <div className="rounded-[8px] bg-white px-4 py-6 text-center">
            <p className="text-[13px] font-bold text-slate-800">
              {activeTab === ACTIVITY_STATUS_TABS.completed
                ? 'هنوز تکلیفی انجام ندادی'
                : 'الان تکلیف انجام‌نشده‌ای نداری'}
            </p>
            <p className="mt-2 text-[11px] text-slate-500">
              {activeTab === ACTIVITY_STATUS_TABS.completed
                ? 'بعد از ثبت تکلیف، اینجا نمایش داده می‌شود.'
                : 'همه تکالیف انجام شده‌اند یا هنوز مورد جدیدی منتشر نشده است.'}
            </p>
          </div>
        ) : (
          visibleHomeworks.map((homework, index) => {
            const viewStatus = getHomeworkViewStatus(homework);
            const status = statusConfig[viewStatus] || statusConfig.published;
            const isSubmitted = viewStatus === 'submitted';
            const cardProps = getListCardProps(viewStatus);

            return (
              <Link
                key={homework.id}
                to={`/homeworks/${homework.id}`}
                className={cardProps.className}
                style={cardProps.style}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    {isSubmitted ? (
                      <span className={`flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-[5px] text-[12px] ${focusTheme.submitted.badge}`}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                      </span>
                    ) : (
                      <span className={`flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-[5px] text-[12px] font-bold ${status.badgeClass}`}>
                        {index + 1}
                      </span>
                    )}

                    <p className="min-w-0 truncate text-[14px] font-semibold text-slate-900">{homework.title || 'بدون عنوان'}</p>
                  </div>

                  <ArrowCircleIcon className={status.iconClass} />
                </div>

                {!isSubmitted && homework.description?.trim() && (
                  <p className="line-clamp-2 text-[13px] leading-6 text-slate-700">
                    {homework.description}
                  </p>
                )}

                <div className={`grid ${isSubmitted ? 'grid-cols-2' : 'grid-cols-3'} gap-2 text-[11px]`}>
                  <div className={focusTheme.meta.chip}>
                    مهلت: {formatHomeworkDate(homework.dueAt)}
                  </div>

                  {!isSubmitted && (
                    <div className={`${focusTheme.meta.chip} text-center`}>
                      {homework.questionCount || 0} سوال
                    </div>
                  )}

                  <div className={`${focusTheme.meta.status} ${status.labelClass}`}>
                    {status.label}
                  </div>
                </div>
              </Link>
            );
          })
        )}
      </div>

      {activeTab === ACTIVITY_STATUS_TABS.remaining && (
        <div className='w-full mx-auto flex flex-col gap-2 items-center justify-center'>
          <p className='text-[13px] font-medium text-slate-800'>
            هر تکلیف رو باز کن، سوالاتش رو حل کن و بعد برو سراغ بعدی :)
          </p>

          {nextHomework && (
            <Link
              to={`/homeworks/${nextHomework.id}`}
              className={`w-11/12 mx-auto text-[14px] font-bold py-2.5 text-center flex items-center justify-center rounded-[8px] ${focusTheme.button.primary}`}
            >
              شروع اولین تکلیف
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

export default HomeworksPage;